import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

// Slide-over catalog picker on the LV and offer editors. A folder rail plus a
// search/tag/sort strip drive a debounced fetch against the catalog; the results render
// with an insert button that posts to catalog_insertions. Insert responses are Turbo
// Streams that replace the tree, so the panel — which lives outside that frame — stays
// open across insertions. Results are also drag sources (clones) sharing the
// "boq-catalog" Sortable group with the tree.
//
// Only the panel chrome ships with the page. The rail, the filter strip and the first
// results are fetched from #picker_panel into the body frame on first open, so a visit
// that never opens the picker does not pay for the rail's facet queries. The server
// renders the filters as selected state, which is why anything that has to change a
// <select> re-renders the body instead of writing to the element — SlimSelect replaces
// the native control and does not observe it.
const FILTER_STORAGE_KEY = "boq-catalog-picker-filters"
const EMPTY_FILTERS = { folderId: "", favorites: "0", tag: "", sort: "" }

export default class extends Controller {
  static targets = [
    "panel",
    "backdrop",
    "body",
    "results",
    "input",
    "folderRail",
    "tagSelect",
    "sortSelect",
    "resetButton",
  ]
  static values = {
    panelUrl: String,
    searchUrl: String,
    documentId: Number,
    offerId: Number,
    invoiceId: Number,
    // Where an insert lands, when a caller named it (the position grid: under its selected row).
    // Empty means the default — the end of the document.
    insertParentId: String,
    insertPosition: String,
  }

  connect() {
    this.timer = null
    this.filters = this.storedFilters()
    this.boundKeydown = this.onKeydown.bind(this)
    this.boundFocus = this.onWindowFocus.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("focus", this.boundFocus)
    this.boundSubmit = this.injectInsertContext.bind(this)
    this.element.addEventListener("submit", this.boundSubmit, true)
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    // Nulled because Stimulus reuses this instance on reconnect, and initDrag() would
    // otherwise destroy the same Sortable twice (its destroy() throws on the second call).
    this.sortable?.destroy()
    this.sortable = null
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("focus", this.boundFocus)
    this.element.removeEventListener("submit", this.boundSubmit, true)
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.isOpen()) this.close()
    else if (this.isOpen() && this.hasInputTarget && event.target === this.inputTarget)
      this.searchKeydown(event)
  }

  // From the search box, without the mouse: the arrows walk the results, Enter inserts the one
  // walked to (its own form, so its quantity field and selection still apply).
  searchKeydown(event) {
    const forms = this.hasResultsTarget
      ? Array.from(this.resultsTarget.querySelectorAll("form"))
      : []
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!forms.length) return
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.activeIndex = Math.min(Math.max((this.activeIndex ?? -1) + step, 0), forms.length - 1)
      this.markActiveResult(forms)
    } else if (event.key === "Enter" && forms[this.activeIndex]) {
      event.preventDefault()
      forms[this.activeIndex].requestSubmit()
    }
  }

  markActiveResult(forms) {
    forms.forEach((form, index) => {
      const row = form.closest('[data-controller~="catalog-entry-row"]') ?? form
      row.classList.toggle("bg-green-50", index === this.activeIndex)
      if (index === this.activeIndex) row.scrollIntoView({ block: "nearest" })
    })
  }

  // The position grid names where its next insert goes (under the selected row). Cleared on
  // close, so a later insert from the toolbar button lands at the end again.
  setInsertContext({ parentId = "", position = "" } = {}) {
    this.insertParentIdValue = String(parentId ?? "")
    this.insertPositionValue = String(position ?? "")
  }

  // Captured, so the fields are in the form before Turbo reads it for the submission.
  injectInsertContext(event) {
    const form = event.target
    if (!this.panelTarget.contains(form)) return

    form.querySelectorAll("[data-insert-context]").forEach((field) => field.remove())
    if (!this.insertPositionValue) return

    for (const [name, value] of [
      ["parent_id", this.insertParentIdValue],
      ["position", this.insertPositionValue],
    ]) {
      const field = document.createElement("input")
      Object.assign(field, { type: "hidden", name, value })
      field.dataset.insertContext = ""
      form.append(field)
    }
  }

  // Bausteine are commonly edited in the catalog tab opened from this panel's header;
  // coming back should show that edit, not a stale list.
  onWindowFocus() {
    if (this.isOpen() && this.loaded) this.fetchResults()
  }

  isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  // Make the result rows drag sources. Sortable only drags a container's DIRECT
  // children, so it must bind to the inner results list (whose children are the
  // rows), not the results target — and it is re-created after every search because
  // that list element is replaced on each fetch.
  initDrag() {
    this.sortable?.destroy()
    const list = this.resultsTarget.querySelector("#boq_catalog_picker_results")
    if (!list) return

    this.sortable = new Sortable(list, {
      group: { name: "boq-catalog", pull: "clone", put: false },
      sort: false,
      draggable: "[data-catalog-entry-id]",
      handle: "[data-catalog-drag-handle]",
      // Pointer-based drag (not native HTML5 DnD): reliable when dragging out of a
      // fixed, scrollable slide-over into the tree.
      forceFallback: true,
      fallbackOnBody: true,
      animation: 150,
    })
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")
    if (this.loaded) {
      if (this.hasInputTarget) this.inputTarget.focus()
      return
    }

    this.bodyTarget.src = this.panelUrl()
    this.loaded = true
  }

  // The body arrives asynchronously, so the search box can only be focused once it is
  // actually there. Reopening an already-loaded panel focuses it in #open instead.
  inputTargetConnected() {
    if (this.isOpen()) this.inputTarget.focus()
  }

  // Results arrive either with the body frame or from a filter fetch. The frame case
  // replaces the target itself, so the drag source has to be rebuilt here; the fetch
  // case only swaps the target's contents and rebuilds it inline.
  resultsTargetConnected() {
    this.initDrag()
    this.activeIndex = null
  }

  // Everything the picker_panel endpoint needs to render the body already filtered:
  // the insert context, plus whichever filters are in play. On first open those come
  // from sessionStorage (the DOM does not exist yet), afterwards from the live strip.
  panelUrl() {
    const url = new URL(this.panelUrlValue, window.location.origin)
    this.applyContext(url)
    this.applyFilters(url)
    return url.toString()
  }

  // Re-render the body from the server. Used wherever a <select> or the rail has to come
  // back in a different state — see the SlimSelect note at the top.
  reloadBody() {
    const url = this.panelUrl()
    if (this.bodyTarget.src === url) this.bodyTarget.reload()
    else this.bodyTarget.src = url
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.backdropTarget.classList.add("hidden")
    this.setInsertContext()
    this.activeIndex = null
    this.dispatch("closed")
  }

  search() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchResults(), 250)
  }

  // Reloads the whole body, not just the results: Bausteine are commonly edited in the
  // catalog tab this panel links to, and the rail's counts go stale with them.
  refresh() {
    this.reloadBody()
  }

  // Change handler of the tag/sort selects.
  filterChanged() {
    this.filters.tag = this.hasTagSelectTarget ? this.tagSelectTarget.value : ""
    this.filters.sort = this.hasSortSelectTarget ? this.sortSelectTarget.value : ""
    this.persistFilters()
    this.fetchResults()
  }

  selectFolder(event) {
    const button = event.currentTarget
    this.filters.folderId = button.dataset.pickerFolder || ""
    this.filters.favorites = button.dataset.pickerFavorites || "0"
    this.markActiveFolder()
    this.filterChanged()
  }

  resetFilters() {
    this.filters = { ...EMPTY_FILTERS }
    // Clear the live controls too: applyFilters prefers them over this.filters, so
    // leaving them set would carry the old tag/sort straight back into the reload. Only
    // the native values matter — the elements themselves are about to be replaced by the
    // server-rendered ones, so SlimSelect never has to be told.
    if (this.hasInputTarget) this.inputTarget.value = ""
    if (this.hasTagSelectTarget) this.tagSelectTarget.value = ""
    if (this.hasSortSelectTarget) this.sortSelectTarget.value = ""
    this.persistFilters()
    this.reloadBody()
  }

  markActiveFolder() {
    if (!this.hasFolderRailTarget) return

    this.folderRailTarget.querySelectorAll("[data-picker-folder]").forEach((button) => {
      const active =
        (button.dataset.pickerFolder || "") === this.filters.folderId &&
        (button.dataset.pickerFavorites || "0") === this.filters.favorites
      button.setAttribute("aria-current", String(active))
    })
  }

  // Results only. Everything that can reach this has the body loaded, except the header's
  // refresh button and a window focus racing the first open — hence the target guard.
  async fetchResults() {
    if (!this.hasResultsTarget) return

    const url = new URL(this.searchUrlValue, window.location.origin)
    this.applyContext(url)
    this.applyFilters(url)
    this.toggleResetButton()

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      })
      if (response.ok) {
        this.resultsTarget.innerHTML = await response.text()
        // The target stays connected, so resultsTargetConnected does not reset the walk: an
        // index into the old list would make Enter insert an unmarked row of the new one.
        this.activeIndex = null
        this.initDrag()
      }
    } catch {
      // Network hiccup — leave the previous results in place rather than clearing.
    }
  }

  // Which document the picker inserts into — the server derives the insert endpoint of
  // each result row from it.
  applyContext(url) {
    if (this.hasDocumentIdValue) url.searchParams.set("document_id", this.documentIdValue)
    if (this.hasOfferIdValue) url.searchParams.set("offer_id", this.offerIdValue)
    if (this.hasInvoiceIdValue) url.searchParams.set("invoice_id", this.invoiceIdValue)
  }

  // The live strip wins once it exists; before that (first open) the values restored
  // from sessionStorage stand in, so the body comes back already filtered.
  applyFilters(url) {
    const tag = this.hasTagSelectTarget ? this.tagSelectTarget.value : this.filters.tag
    const sort = this.hasSortSelectTarget ? this.sortSelectTarget.value : this.filters.sort
    const query = this.hasInputTarget ? this.inputTarget.value : ""

    url.searchParams.set("q", query)
    if (this.filters.folderId) url.searchParams.set("folder_id", this.filters.folderId)
    if (this.filters.favorites === "1") url.searchParams.set("favorites", "1")
    if (tag) url.searchParams.set("tag", tag)
    if (sort) url.searchParams.set("sort", sort)
  }

  toggleResetButton() {
    if (!this.hasResetButtonTarget) return

    const active =
      this.inputTarget.value !== "" ||
      this.filters.folderId !== "" ||
      this.filters.favorites === "1" ||
      (this.hasTagSelectTarget && this.tagSelectTarget.value !== "")
    this.resetButtonTarget.classList.toggle("hidden", !active)
  }

  // Filters survive closing the panel (and a full page load), so working through one
  // folder does not mean re-picking it for every position. The search text is left out
  // deliberately — a query is about one position, a folder is about a session's work.
  persistFilters() {
    try {
      window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(this.filters))
    } catch {
      // Private mode / quota — filters simply do not persist.
    }
  }

  storedFilters() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY) || "null")
      return stored ? { ...EMPTY_FILTERS, ...stored } : { ...EMPTY_FILTERS }
    } catch {
      return { ...EMPTY_FILTERS }
    }
  }
}
